import React, { useMemo, useState } from 'react';

import { defaultPlanFields, validatePlanDischarge, planDischargeErrorMessage } from './planDischargeModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60';
const btnDanger = 'px-4 py-2 rounded-lg border border-red-300 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 text-sm font-semibold disabled:opacity-60';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';

/**
 * PlanDischargeDialog (S4/FE-2, stories.md Screen B) — the "Plan discharge"
 * workspace button's own modal: record the discharge date+time the doctor
 * advised, before discharge is initiated. Centred, ConfirmationModal-family
 * overlay (same `overlay`/`panel` visual convention `RecordDeathDialog`
 * uses) — never a native browser date-input dialog, never rendered inline
 * under the charge list (build-plan C2/FE-2).
 *
 * `onSave` (not a direct API import) mirrors the DI convention every other
 * admission dialog in this folder follows (`RecordDeathDialog.onSave`,
 * `DischargeDialog.onComplete`): the caller owns the network call (ONE PATCH
 * `/admissions/{id}`, C2) so it can also patch its own local admission state
 * from the response in one place. The SAME `onSave` drives both SET/EDIT
 * (`{planned_discharge_at: isoString}`) and CLEAR (`{planned_discharge_at:
 * null}`) — no second endpoint.
 *
 * @param admission          { admission_id, patient_name, ip_number, admitted_at,
 *                              planned_discharge_at }
 * @param onClose            ()
 * @param onSave(body)        → { success, data?, error?, code?, ...extras }
 * @param onSaved(admission)  called after a successful SET/EDIT — the FULL
 *                              `AdmissionResponse` (C2 — carries the new
 *                              `planned_discharge_at`)
 * @param onCleared(admission) called after a successful CLEAR — the FULL
 *                              `AdmissionResponse`; kept distinct from
 *                              `onSaved` so the caller fires the
 *                              "Discharge plan cleared." toast (AC-I5) only
 *                              on this path, never on a plain save
 */
export default function PlanDischargeDialog({ admission, onClose, onSave, onSaved, onCleared }) {
  const admittedAt = admission.admitted_at;
  const existingPlan = admission.planned_discharge_at || null;
  const [fields, setFields] = useState(() => defaultPlanFields(existingPlan));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Re-runs on EVERY date/time edit, whichever field changed (AC-I2/AC-I3) —
  // neither field is ever silently rewritten to "fix" the other.
  const validation = useMemo(
    () => validatePlanDischarge({ date: fields.date, time: fields.time, admittedAt }),
    [fields.date, fields.time, admittedAt],
  );
  const canClear = !!existingPlan;
  const saveDisabled = busy || !validation.valid;

  const setDate = (date) => setFields((f) => ({ ...f, date }));
  const setTime = (time) => setFields((f) => ({ ...f, time }));

  const runSave = async () => {
    if (saveDisabled) return;
    setBusy(true);
    setError(null);
    const res = await onSave({ planned_discharge_at: validation.plannedIso });
    setBusy(false);
    if (res.success) {
      if (typeof onSaved === 'function') onSaved(res.data);
      onClose();
      return;
    }
    setError(planDischargeErrorMessage(res));
  };

  const runClear = async () => {
    if (busy || !canClear) return;
    setBusy(true);
    setError(null);
    const res = await onSave({ planned_discharge_at: null });
    setBusy(false);
    if (res.success) {
      if (typeof onCleared === 'function') onCleared(res.data);
      onClose();
      return;
    }
    setError(planDischargeErrorMessage(res));
  };

  return (
    <div className={overlayCls} data-testid="plan-discharge-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Plan discharge</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>

        <div className="p-4 space-y-4">
          <div className="text-[12.5px] text-slate-500 dark:text-slate-400">
            {admission.patient_name || admission.patient_id} · {admission.ip_number}
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="plan-discharge-error">
              {error}
            </div>
          )}

          <div>
            <label className={labelCls} htmlFor="plan-discharge-date">Planned date</label>
            <input
              id="plan-discharge-date"
              className={inputCls}
              type="date"
              value={fields.date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="plan-discharge-date"
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="plan-discharge-time">Planned time</label>
            <input
              id="plan-discharge-time"
              className={inputCls}
              type="time"
              value={fields.time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="plan-discharge-time"
            />
          </div>

          {validation.error && (
            <p className="text-[12px] text-red-700 dark:text-red-300" data-testid="plan-discharge-validation-error">
              {validation.error}
            </p>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
          <button type="button" className={btnDanger} onClick={runClear} disabled={busy || !canClear} data-testid="plan-discharge-clear">
            Clear plan
          </button>
          <div className="flex gap-2">
            <button type="button" className={btnSecondary} onClick={onClose} disabled={busy} data-testid="plan-discharge-cancel">
              Cancel
            </button>
            <button type="button" className={btnPrimary} onClick={runSave} disabled={saveDisabled} data-testid="plan-discharge-save">
              {busy ? 'Saving…' : 'Save plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
